import { useEffect, useRef, useState } from 'react';
import { Download, Share2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

type Stats = { lessons_this_week: number; avg_streak: number; players: number };

const W = 1080, H = 1080;

function cssVar(name: string, fallback: string) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `hsl(${v})` : fallback;
}

/** Shareable team progress card drawn on an inline canvas from real team data. */
export function TeamShareCard({ teamId, teamName, sport }: { teamId: string; teamName: string; sport: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    if (!open) return;
    setStats(null);
    supabase.rpc('get_team_share_stats' as never, { p_team_id: teamId } as never).then(({ data, error }) => {
      if (error) { toast.error('Could not load team stats'); setOpen(false); return; }
      setStats(data as unknown as Stats);
    });
  }, [open, teamId]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !stats) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const bg = cssVar('--background', '#0b1220');
    const fg = cssVar('--foreground', '#ffffff');
    const primary = cssVar('--primary', '#007bff');
    const muted = cssVar('--muted-foreground', '#94a3b8');

    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(W * 0.85, H * 0.1, 0, W * 0.85, H * 0.1, W * 0.8);
    g.addColorStop(0, primary); g.addColorStop(1, 'transparent');
    ctx.globalAlpha = 0.35; ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.globalAlpha = 1;

    const emoji = sport === 'basketball' ? '🏀' : sport === 'soccer' ? '⚽' : sport === 'baseball' ? '⚾' : '🏈';
    ctx.textBaseline = 'top';
    ctx.font = '120px sans-serif'; ctx.fillText(emoji, 80, 90);
    ctx.fillStyle = muted; ctx.font = '600 36px "Space Grotesk", sans-serif';
    ctx.fillText(`${sport.toUpperCase()} · THIS WEEK`, 80, 250);
    ctx.fillStyle = fg; ctx.font = '700 84px "Space Grotesk", sans-serif';
    let name = teamName;
    while (ctx.measureText(name).width > W - 160 && name.length > 3) name = name.slice(0, -2);
    ctx.fillText(name === teamName ? name : `${name}…`, 80, 300);

    const box = (x: number, label: string, value: string) => {
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.beginPath(); ctx.roundRect(x, 500, 440, 340, 32); ctx.fill();
      ctx.fillStyle = primary; ctx.font = '700 150px "Space Grotesk", sans-serif'; ctx.fillText(value, x + 40, 550);
      ctx.fillStyle = muted; ctx.font = '500 36px Inter, sans-serif'; ctx.fillText(label, x + 40, 750);
    };
    box(80, 'Lessons completed', String(stats.lessons_this_week));
    box(560, 'Average streak (days)', String(stats.avg_streak));

    ctx.fillStyle = muted; ctx.font = '500 30px Inter, sans-serif';
    ctx.fillText(`${stats.players} player${stats.players === 1 ? '' : 's'}`, 80, 900);
    ctx.globalAlpha = 0.7; ctx.fillStyle = fg; ctx.font = '700 30px "Space Grotesk", sans-serif';
    ctx.textAlign = 'right'; ctx.fillText('⚡ Sportara', W - 60, H - 80); ctx.textAlign = 'left'; ctx.globalAlpha = 1;
  }, [stats, teamName, sport]);

  const download = () => {
    const c = canvasRef.current; if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `${teamName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-progress.png`;
    a.click();
    toast.success('Card downloaded');
  };

  return (
    <div className="glass-card p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display font-bold text-sm flex items-center gap-2 text-foreground">
          <Share2 className="w-4 h-4 text-primary" /> Share team progress
        </h3>
        <Button size="sm" variant="outline" onClick={() => setOpen(o => !o)} aria-expanded={open} className="font-display font-bold">
          {open ? 'Hide card' : 'Create card'}
        </Button>
      </div>
      {open && (
        <div className="space-y-3">
          {!stats && <p className="text-xs text-muted-foreground font-body">Loading team stats…</p>}
          <canvas ref={canvasRef} width={W} height={H} className={`w-full max-w-sm mx-auto rounded-xl ${stats ? 'block' : 'hidden'}`} aria-label={`${teamName} progress card`} />
          {stats && (
            <div className="flex justify-center">
              <Button size="sm" onClick={download} className="font-display font-bold"><Download className="w-4 h-4 mr-1.5" /> Download image</Button>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground font-body text-center">Lessons counted over the last 7 days. Streak averages count only players active in the last day.</p>
        </div>
      )}
    </div>
  );
}
